import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { getSurvey, autoSaveSurvey, submitSurvey } from '../services/surveyService';
import './SurveyPage.css';

const SurveyPage = () => {
  const { user } = useAuth();
  const [questions, setQuestions] = useState([]);
  const [answers, setAnswers] = useState({});
  const [additionalData, setAdditionalData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [message, setMessage] = useState('');
  const [messageType, setMessageType] = useState('');
  const [lastSaved, setLastSaved] = useState(null);
  const autoSaveTimerRef = useRef(null);
  const navigate = useNavigate();
  const isSubmitted = user?.status === 'submitted';

  useEffect(() => {
    loadSurvey();
    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, []);

  useEffect(() => {
    // 10초마다 자동 저장
    autoSaveTimerRef.current = setInterval(() => {
      handleAutoSave();
    }, 10000);

    return () => {
      if (autoSaveTimerRef.current) {
        clearInterval(autoSaveTimerRef.current);
      }
    };
  }, [answers]);

  const loadSurvey = async () => {
    try {
      const data = await getSurvey();
      setQuestions(data.questions || []);
      setAnswers(data.answers || {});
      setAdditionalData(data.additionalData || {});
    } catch (error) {
      console.error('설문지 로드 실패:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleAutoSave = async () => {
    if (isSubmitted) return;
    if (Object.keys(answers).length === 0 && Object.keys(additionalData).length === 0) return;

    try {
      // info, title 타입 질문은 answers에서 제외
      const answersToSave = {};
      questions.forEach(q => {
        const questionId = q.id;
        const questionType = q.questionType || q.question_type;
        if (answers[questionId] && questionType !== 'info' && questionType !== 'title') {
          answersToSave[questionId] = answers[questionId];
        }
      });
      
      if (Object.keys(answersToSave).length > 0 || Object.keys(additionalData).length > 0) {
        await autoSaveSurvey(answersToSave, additionalData);
        setLastSaved(new Date());
      }
    } catch (error) {
      console.error('자동 저장 실패:', error);
    }
  };

  const handleInputFieldChange = (questionId, fieldName, value) => {
    if (isSubmitted) return;
    setAdditionalData(prev => {
      const questionData = prev[questionId] || {};
      return {
        ...prev,
        [questionId]: {
          ...questionData,
          [fieldName]: value
        }
      };
    });
  };

  const handleAnswerChange = (questionId, value) => {
    if (isSubmitted) return;
    const newAnswers = { ...answers, [questionId]: value };
    setAnswers(newAnswers);

    // '아니오' 선택 시 하위 질문 답변 및 입력 필드 초기화
    if (value === 'no') {
      const question = questions.find((q) => q.id === questionId);
      if (question?.subQuestions) {
        question.subQuestions.forEach((subQ) => {
          delete newAnswers[subQ.id];
        });
      }
      // 입력 필드 데이터도 초기화
      setAdditionalData(prev => {
        const newData = { ...prev };
        delete newData[questionId];
        return newData;
      });
    }
  };

  const handleBlur = () => {
    // 포커스 아웃 시 자동 저장
    handleAutoSave();
  };

  const isQuestionVisible = (question) => {
    // 조건부 질문 체크
    if (question.dependsOn) {
      // dependsOn은 ID 또는 객체일 수 있음
      const parentId = typeof question.dependsOn === 'object' 
        ? question.dependsOn.id || question.dependsOn.questionId
        : question.dependsOn;
      const parentAnswer = answers[parentId];
      const requiredAnswer = question.dependsAnswer || question.depends_answer || 'yes';
      
      // 부모 답변이 없으면 표시하지 않음
      if (!parentAnswer) {
        return false;
      }
      
      // dependsAnswer가 'yes'인 경우
      if (requiredAnswer === 'yes') {
        return parentAnswer === 'yes';
      }
      // dependsAnswer가 'no'인 경우
      if (requiredAnswer === 'no') {
        return parentAnswer === 'no';
      }
      // dependsAnswer가 특정 값(선택지 옵션)인 경우
      return parentAnswer === requiredAnswer;
    }
    return true;
  };

  const isQuestionRequired = (question) => {
    if (!question.required) return false;

    // 하위 질문은 부모가 조건에 맞을 때만 필수
    if (question.dependsOn) {
      const parentId = typeof question.dependsOn === 'object' 
        ? question.dependsOn.id || question.dependsOn.questionId
        : question.dependsOn;
      const parentAnswer = answers[parentId];
      const requiredAnswer = question.dependsAnswer || 'yes';
      
      if (requiredAnswer === 'yes' && parentAnswer !== 'yes') {
        return false;
      }
      if (requiredAnswer === 'no' && parentAnswer !== 'no') {
        return false;
      }
    }

    return true;
  };

  const isAllRequiredAnswered = () => {
    return questions.every((question) => {
      const questionType = question.questionType || question.question_type;
      // 정보성 질문, 타이틀은 답변 불필요
      if (questionType === 'info' || questionType === 'title') {
        return true;
      }
      if (!isQuestionVisible(question) || !isQuestionRequired(question)) {
        return true;
      }
      return answers[question.id] !== undefined && answers[question.id] !== '';
    });
  };

  const handleSubmit = async () => {
    if (isSubmitted) return;
    if (!isAllRequiredAnswered()) {
      setMessage('모든 필수 질문에 답변해주세요.');
      setMessageType('error');
      return;
    }

    setSubmitting(true);
    try {
      await submitSurvey(answers, additionalData);
      navigate('/completed');
    } catch (error) {
      setMessage('설문지 제출에 실패했습니다.');
      setMessageType('error');
      console.error('설문지 제출 실패:', error);
    } finally {
      setSubmitting(false);
    }
  };

  const renderQuestion = (question) => {
    if (!isQuestionVisible(question)) return null;

    const answer = answers[question.id] || '';
    const questionAdditionalData = additionalData[question.id] || {};
    
    // questionType 또는 question_type 둘 다 체크 (serializer에서 questionType으로 변환됨)
    const questionType = question.questionType || question.question_type;
    const isInfoType = questionType === 'info';
    const isTitleType = questionType === 'title';
    const isSelectType = questionType === 'select';
    const inputFields = question.inputFields || question.input_fields || [];

    // 타이틀인 경우
    if (isTitleType) {
      return (
        <div key={question.id} className="question-title">
          <h2>
            <span className="title-triangle outline" />
            {question.text}
          </h2>
        </div>
      );
    }

    // 정보성 질문인 경우 (question_type이 'info') - 보라색 배경 박스
    if (isInfoType) {
      return (
        <div key={question.id} className="question-item info-box">
          <div className="info-box-content">
            {question.text}
          </div>
          {question.subQuestions && question.subQuestions.length > 0 && (
            <div className="sub-questions">
              {question.subQuestions.map((subQ) => renderQuestion(subQ))}
            </div>
          )}
        </div>
      );
    }

    // 선택형 질문 (options 사용)
    if (isSelectType) {
      const options = question.options || [];
      return (
        <div key={question.id} className="question-item">
          <div className="question-header">
            <h3>
              Q: {question.text}
              {isQuestionRequired(question) && <span className="required">*</span>}
            </h3>
          </div>
          <div className="question-options">
            {options.map((option, index) => (
              <label key={index} className="radio-label">
                <input
                  type="radio"
                  name={`question-${question.id}`}
                  value={option}
                  checked={answer === option}
                  onChange={(e) => handleAnswerChange(question.id, e.target.value)}
                  onBlur={handleBlur}
                  disabled={isSubmitted}
                />
                <span>{option}</span>
              </label>
            ))}
          </div>
          {/* 선택한 값에 따라 하위 질문 표시 */}
          {answer && question.subQuestions && question.subQuestions.length > 0 && (
            <div className="conditional-content">
              <div className="sub-questions">
                {question.subQuestions
                  .filter(subQ => {
                    // dependsAnswer가 부모 답변과 일치하는 하위 질문만 표시
                    const requiredAnswer = subQ.dependsAnswer || subQ.depends_answer;
                    return !requiredAnswer || requiredAnswer === answer;
                  })
                  .map((subQ) => renderQuestion(subQ))}
              </div>
            </div>
          )}
        </div>
      );
    }

    // 일반 질문 (예/아니오)
    return (
      <div key={question.id} className="question-item">
        <div className="question-header">
          <h3>
            Q: {question.text}
            {isQuestionRequired(question) && <span className="required">*</span>}
          </h3>
        </div>
        <div className="question-options">
          <label className="radio-label">
            <input
              type="radio"
              name={`question-${question.id}`}
              value="yes"
              checked={answer === 'yes'}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              onBlur={handleBlur}
              disabled={isSubmitted}
            />
            <span>예</span>
          </label>
          <label className="radio-label">
            <input
              type="radio"
              name={`question-${question.id}`}
              value="no"
              checked={answer === 'no'}
              onChange={(e) => handleAnswerChange(question.id, e.target.value)}
              onBlur={handleBlur}
              disabled={isSubmitted}
            />
            <span>아니오</span>
          </label>
        </div>
        {/* '예' 선택 시 입력 필드 표시 (input_fields가 있는 경우) */}
        {answer === 'yes' && inputFields.length > 0 && (
          <div className="input-fields-container">
            {inputFields.map((fieldName, index) => {
              const fieldLabel = fieldName.replace(/_/g, ' '); // 언더스코어를 공백으로
              const fieldValue = questionAdditionalData[fieldName] || '';
              return (
                <div key={index} className="input-field-group">
                  <label className="input-field-label">
                    {fieldLabel}
                    {isQuestionRequired(question) && <span className="required">*</span>}
                  </label>
                  <input
                    type="text"
                    className="input-field"
                    value={fieldValue}
                    onChange={(e) => handleInputFieldChange(question.id, fieldName, e.target.value)}
                    onBlur={handleBlur}
                    placeholder={`${fieldLabel}을(를) 입력하세요`}
                    readOnly={isSubmitted}
                  />
                </div>
              );
            })}
          </div>
        )}
        {/* '예' 선택 시 하위 질문 및 정보 박스 표시 */}
        {answer === 'yes' && question.subQuestions && question.subQuestions.length > 0 && (
          <div className="conditional-content">
            <div className="sub-questions">
              {question.subQuestions.map((subQ) => renderQuestion(subQ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  if (loading) {
    return <div className="loading">로딩 중...</div>;
  }

  const topLevelQuestions = questions.filter((q) => !q.dependsOn);
  const sortedTopLevelQuestions = [...topLevelQuestions].sort((a, b) => {
    const orderA = a.order ?? 0;
    const orderB = b.order ?? 0;
    if (orderA !== orderB) return orderA - orderB;
    const numberA = a.number ?? 0;
    const numberB = b.number ?? 0;
    return numberA - numberB;
  });
  const introTitleQuestion = sortedTopLevelQuestions.find(
    (q) => (q.questionType || q.question_type) === 'title'
  );
  const introInfoQuestion = sortedTopLevelQuestions.find(
    (q) => (q.questionType || q.question_type) === 'info'
  );
  const introTitleText =
    introTitleQuestion?.text || '근로소득 외 타(他) 종합소득 신고 의무 확인';
  const introInfoText =
    introInfoQuestion?.text ||
    '소득세법에서 규정하고 있는 종합소득은 이자소득, 배당소득, 사업소득, 근로소득, 연금소득 그리고 기타소득을 합산한 것입니다. 근로소득 외 다른 종합소득에 대한 합산 의무가 있는지 검토하고 적정한 신고를 도와드릴 수 있도록 설문지를 성실히 답하여 주시기 바랍니다. 질문은 모두 2025년 1월 1일부터 2025년 12월 31일까지의 과세기간 동안 발생한 내역을 확인하기 위함입니다. 근로소득 외 합산되어야 할 다른 종합소득이 있을 것으로 검토되는 경우, 저희 담당자가 증빙자료 제출 요청을 위해 별도로 연락을 드릴 것입니다.';
  const introTitleId = introTitleQuestion?.id;
  const introInfoId = introInfoQuestion?.id;

  return (
    <div className="survey-page">
      <div className="survey-header">
        <div className="survey-title">
          <h1>
            <span className="title-triangle solid" />
            {introTitleText}
          </h1>
        </div>
        <div className="survey-intro">{introInfoText}</div>
        {lastSaved && (
          <div className="save-indicator">
            마지막 저장: {lastSaved.toLocaleTimeString()}
          </div>
        )}
      </div>

      <div className="survey-content">
        {sortedTopLevelQuestions
          .filter((q) => q.id !== introTitleId && q.id !== introInfoId)
          .map((question) => renderQuestion(question))}
      </div>

      <div className="survey-footer">
        {message && <div className={`survey-message ${messageType}`}>{message}</div>}
        <button
          onClick={handleSubmit}
          className="submit-btn"
          disabled={submitting || isSubmitted || !isAllRequiredAnswered()}
        >
          {isSubmitted ? '제출 완료' : (submitting ? '제출 중...' : '제출하기')}
        </button>
      </div>
    </div>
  );
};

export default SurveyPage;

